// Renderer interface (IMPLEMENTATION §2.1, §2.9): init, frame(alpha), onEvents, relayout, rebuildFromState,
// destroy. Two implementations: PhaserRenderer (BootScene bake → FactoryScene, or the M0 SpikeScene with
// ?spike=1) and NullRenderer (?render=none). The renderer is a thin view of the sim: it reads the state
// through a RenderSource and sim/query.ts, never mutates it, and never imports sim/systems or sim/rules.
import type { Cell, Layout } from '../layout/computeLayout.ts';
import type { ViewportInfo } from '../platform/viewport.ts';
import type { SimDefs } from '../sim/defs.ts';
import type { SimEvent } from '../sim/events.ts';
import type { SimState } from '../sim/state.ts';

export type RendererKind = 'phaser' | 'none';

/** One canvas pixel, 0–255 per channel. */
export interface Rgba {
  r: number;
  g: number;
  b: number;
  a: number;
}

/** One `?spike=1` capability probe: ok plus a short human note (value read back, error text, …). */
export interface SpikeCheck {
  ok: boolean;
  note: string;
}

/** `__gm.spike()` (M0 acceptance): one entry per Phaser 4 API the production renderer relies on. */
export interface SpikeReport {
  renderer: RendererKind;
  webgl: boolean;
  mipmaps: boolean;
  checks: {
    addCanvasFrames: SpikeCheck;
    image: SpikeCheck;
    tint: SpikeCheck;
    setCrop: SpikeCheck;
    tween: SpikeCheck;
    particlesExplode: SpikeCheck;
    cameraZoomScroll: SpikeCheck;
    scaleResizeZoom: SpikeCheck;
    mipmaps: SpikeCheck;
    webgl: SpikeCheck;
  };
  gl: { version: string; renderer: string; maxTextureSize: number } | null;
  device: { ua: string; dpr: number; dprEff: number; iw: number; ih: number };
  ms: number;
}

/**
 * M6 input overlay kinds (UX §3.9): merge partner (gold, pulsing), insert / deliver target (mint), empty
 * floor (faint outline), the selected item (blue, pulsing), the dragged item's home cell (dashed outline),
 * a paint stroke's placed cells and its refused end / unaffordable tail (red).
 */
export type GlowKind = 'merge' | 'valid' | 'floor' | 'select' | 'home' | 'path' | 'pathBad';

export interface InputGlow {
  x: number;
  y: number;
  kind: GlowKind;
}

/** What the input layer shows on the board (M6): glows, the dragged entity hidden, the selection lifted. */
export interface InputOverlay {
  glows: InputGlow[];
  /** Entity (item or building) whose sprite is hidden while its DOM ghost is dragged. */
  hidden: number | null;
  /** The tap-selected item, lifted by `cells` of a cell (ui.json input.selectLiftPx / cellCss). */
  lifted: { id: number; cells: number } | null;
}

export const NO_OVERLAY: InputOverlay = { glows: [], hidden: null, lifted: null };

/** Where the renderer reads the live simulation (the controller's current sim; swapped on load). */
export interface RenderSource {
  state(): SimState;
  defs(): SimDefs;
}

/** View kinds `debug.count` knows besides Phaser object types ('Image', 'Text', 'Graphics', …). */
export type ViewKind =
  | 'item'
  | 'carry'
  | 'building'
  | 'stall'
  | 'galleryDry'
  | 'fx'
  | 'galleryFx'
  | 'strayCrop'
  | 'missing';

/** `__gm.render` diagnostics backing (M5). */
export interface RenderDebug {
  /** Active views of a kind (`item` counts floor items + chute carries), or display-list objects by Phaser type. */
  count(kind: string): number;
  /** Client CSS px of the drawn centre of item `id` (floor or chute carry), else of building `id`; null if none. */
  debugPos(id: number): { x: number; y: number } | null;
  /** Freezes every tween `ms` from now (null resumes): mid-animation screenshots (M10). */
  freezeAt(ms: number | null): void;
  /** Resolves once no tween, FX or camera move is running and two more frames were drawn. */
  idle(): Promise<void>;
  /** JS time (ms) of the most recent view syncs (render-sync @perf metric). */
  syncMs(): number[];
  /** M13: frames drawn since boot (the Low-quality 30 fps check: the Phaser loop's own frame limit). */
  frames(): number;
  /** Cosmetic events this renderer received through `onEvents` (the silent-advance spy). */
  eventsReceived(): number;
}

export interface Renderer {
  readonly kind: RendererKind;
  /** The sim to draw; may be called again (load / loadSim) — views are rebuilt on the next frame. */
  bind(src: RenderSource): void;
  init(layout: Layout, vp: ViewportInfo): Promise<void>;
  /** New layout: snap the camera, or tween it (`animate`: an expansion, render.expansionTweenMs). */
  relayout(layout: Layout, vp: ViewportInfo, animate?: boolean): void;
  /**
   * Registers the per-frame callback: the FactoryScene calls it from `update` (before Phaser renders), the
   * NullRenderer from its own requestAnimationFrame loop. It receives performance.now().
   */
  drive(onFrame: (nowMs: number) => void): void;
  /** Syncs the views with the state; `alpha` ∈ [0, 1) is the loop's fraction of the next tick (RenderSync). */
  frame(alpha: number): void;
  /** Cosmetic events of the ticks / commands since the last frame (spawn, merge, insert, fire, …). */
  onEvents(events: readonly SimEvent[]): void;
  /** Drops every view and cosmetic effect and rebuilds from the current state (after a silent advance / load). */
  rebuildFromState(): void;
  /** Highlighted cell (spike tap / drag target glow); null clears it. */
  setHighlight(cell: Cell | null): void;
  /** M6: valid-target glows, the hidden dragged entity, the lifted selection (replaces the previous one). */
  setInputOverlay(o: InputOverlay): void;
  /** `__gm.render` diagnostic: the highlighted cell as the scene object shows it (visible + position). */
  highlighted(): Cell | null;
  /** `__gm.render` diagnostic: the canvas pixel at client CSS px after the next frame (null: no canvas). */
  pixelAt(clientX: number, clientY: number): Promise<Rgba | null>;
  /** Runs the Phaser 4 capability probe (`?spike=1`). */
  spike(): Promise<SpikeReport>;
  /** The game canvas, or null for the NullRenderer. */
  canvas(): HTMLCanvasElement | null;
  /** M7 Low quality (§2.9 "caps the frame rate at 30"): frames per second, 0 = no limit. */
  setFpsLimit(fps: number): void;
  readonly debug: RenderDebug;
  destroy(): void;
}

export function emptySpikeReport(
  renderer: RendererKind,
  vp: ViewportInfo,
  note: string,
): SpikeReport {
  const no = (): SpikeCheck => ({ ok: false, note });
  return {
    renderer,
    webgl: false,
    mipmaps: false,
    checks: {
      addCanvasFrames: no(),
      image: no(),
      tint: no(),
      setCrop: no(),
      tween: no(),
      particlesExplode: no(),
      cameraZoomScroll: no(),
      scaleResizeZoom: no(),
      mipmaps: no(),
      webgl: no(),
    },
    gl: null,
    device: {
      ua: typeof navigator === 'undefined' ? '' : navigator.userAgent,
      dpr: vp.dpr,
      dprEff: vp.dprEff,
      iw: vp.iw,
      ih: vp.ih,
    },
    ms: 0,
  };
}
